/**
 * THE projection loop. One implementation, replacing three:
 *   - calculateBalanceOnDate        (~300 lines)
 *   - generateBalanceProjections    (~370 lines, a near-verbatim copy)
 *   - Calendar.tsx's hand-rolled backward replay (~200 lines, a third variant
 *     that ignored payment types entirely and seeded itself from a
 *     localStorage "historical base balance")
 *
 * Emits one LedgerDay per calendar day in [from, to]. `from` MAY precede
 * `openingDate`: the balance is replayed backwards by inverting each day's
 * net flow, which is what lets the Calendar show past days without the
 * localStorage hack.
 *
 * Every event carries a structured `source` discriminant. Nothing downstream
 * should ever parse a description string to work out what a transaction was —
 * that is how `Calendar.getRemainingBalanceAfterPayment` came to break on any
 * card whose name contained the word "Payment".
 */

import { CreditCard, Expense, Income, Loan, PayoffPlan, PaymentStatus, ScheduleException } from '../../types';
import { addDays, daysBetween, startOfDay, toDayKey, toLocalDay } from './dates';
import { CardPaymentEvent, CardPaymentKind, projectCardPayments } from './cardLedger';
import { expenseOccursOn, incomeOccursOn, loanPaymentOccursOn } from './recurrence';

export type LedgerSource =
  | { kind: 'income'; incomeId: string }
  | { kind: 'expense'; expenseId: string; category?: string }
  | { kind: 'loan'; loanId: string }
  | {
      kind: 'cardPayment';
      cardId: string;
      planId: string | null;
      paymentId: string | null;
      paymentKind: CardPaymentKind;
      isFinal: boolean;
      remainingAfter: number;
      status: PaymentStatus;
    };

export interface LedgerEvent {
  id: string;
  date: Date;
  /** Kept as income/expense to match the shape render sites already use. */
  type: 'income' | 'expense';
  /** Always positive; `type` carries the direction. */
  amount: number;
  label: string;
  source: LedgerSource;
}

export interface LedgerDay {
  date: Date;
  /** End-of-day balance. */
  balance: number;
  events: LedgerEvent[];
  /** Projected balance is at or below the user's threshold (and threshold > 0). */
  belowThreshold: boolean;
  isNegative: boolean;
  /** True for days reconstructed by walking backwards from `openingDate`.
   *  Card activity before a card's own balanceAsOf is unknown, so these are
   *  best-effort rather than authoritative. */
  isHistorical: boolean;
}

export interface BuildLedgerInput {
  openingBalance: number;
  /** The balance is true as of the END of this day. */
  openingDate: Date;
  from: Date;
  to: Date;
  incomes?: Income[];
  expenses?: Expense[];
  loans?: Loan[];
  /** Occurrences moved off the day their rule generates. */
  scheduleExceptions?: ScheduleException[];
  creditCards?: CreditCard[];
  plans?: PayoffPlan[];
  lowBalanceThreshold?: number;
  /** Omit these cards' payments entirely. The plan builder precomputes an
   *  "everything except the card being planned" ledger once, then composes
   *  each candidate schedule onto it — otherwise the live threshold warning
   *  would rebuild the whole ledger on every keystroke. */
  excludeCardIds?: string[];
  /** Splice in a candidate (unsaved) schedule. */
  extraCardEvents?: CardPaymentEvent[];
}

function cardEventToLedgerEvent(e: CardPaymentEvent): LedgerEvent {
  const suffix =
    e.paymentKind === 'MINIMUM'
      ? 'Min Payment'
      : e.paymentKind === 'EXTRA'
        ? 'Extra Payment'
        : e.paymentKind === 'FINAL'
          ? 'Final Payment'
          : 'Payment';
  return {
    id: e.paymentId ?? `min-${e.cardId}-${toDayKey(e.date)}`,
    date: e.date,
    type: 'expense',
    amount: e.amount,
    label: `${e.cardName} ${suffix}`,
    source: {
      kind: 'cardPayment',
      cardId: e.cardId,
      planId: e.planId,
      paymentId: e.paymentId,
      paymentKind: e.paymentKind,
      isFinal: e.isFinal,
      remainingAfter: e.remainingAfter,
      status: e.status,
    },
  };
}

export function buildLedger(input: BuildLedgerInput): LedgerDay[] {
  const {
    openingBalance,
    incomes = [],
    expenses = [],
    loans = [],
    scheduleExceptions = [],
    creditCards = [],
    plans = [],
    lowBalanceThreshold = 0,
    excludeCardIds = [],
    extraCardEvents = [],
  } = input;

  const from = startOfDay(input.from);
  const to = startOfDay(input.to);
  const openingDate = startOfDay(input.openingDate);
  if (to < from) return [];

  // The balance is only known at `openingDate`, so every day between the
  // anchor and the requested window must be walked even when it falls outside
  // that window — otherwise those transactions are silently skipped and the
  // window starts from the wrong number. Compute over the union, slice after.
  //
  // This is what made paging from August to September jump the balance: with
  // an anchor of Aug 26 and a September grid starting Aug 30, the four days
  // in between were dropped.
  const computeFrom = openingDate < from ? openingDate : from;
  const computeTo = openingDate > to ? openingDate : to;

  const excluded = new Set(excludeCardIds);

  // --- Card payments ------------------------------------------------------
  // Projected per card from that card's own balanceAsOf anchor, then indexed
  // by day. Cards are projected through `to` regardless of `from`, because a
  // card's balance trajectory depends on every payment since its anchor.
  const byDay = new Map<string, LedgerEvent[]>();
  const push = (date: Date, event: LedgerEvent) => {
    const key = toDayKey(date);
    const list = byDay.get(key);
    if (list) list.push(event);
    else byDay.set(key, [event]);
  };

  for (const card of creditCards) {
    if (excluded.has(card.id)) continue;
    const events = projectCardPayments({ card, plans, through: computeTo });
    for (const e of events) {
      if (e.date < computeFrom || e.date > computeTo) continue;
      push(e.date, cardEventToLedgerEvent(e));
    }
  }
  for (const e of extraCardEvents) {
    if (e.date < computeFrom || e.date > computeTo) continue;
    push(e.date, cardEventToLedgerEvent(e));
  }

  // --- Recurring cash flows ----------------------------------------------
  //
  // A moved occurrence is drawn twice over: the rule still fires on its
  // original day, so that day SKIPS it, and the day it moved to emits it. The
  // rule itself is never consulted for the new day — an exception whose
  // original day no longer fires (the rule changed underneath it) simply stops
  // applying, rather than conjuring an occurrence from nothing.
  const movedAway = new Set<string>();
  const movedOnto = new Map<string, ScheduleException[]>();
  for (const ex of scheduleExceptions) {
    const from = startOfDay(toLocalDay(ex.originalDate));
    const to = startOfDay(toLocalDay(ex.newDate));
    movedAway.add(`${ex.kind}:${ex.sourceId}:${toDayKey(from)}`);
    const key = toDayKey(to);
    const list = movedOnto.get(key);
    const entry = { ...ex, originalDate: from, newDate: to };
    if (list) list.push(entry);
    else movedOnto.set(key, [entry]);
  }
  const isMovedAway = (kind: string, id: string, date: Date) =>
    movedAway.has(`${kind}:${id}:${toDayKey(date)}`);

  const incomeEvent = (income: Income, date: Date, on: Date): LedgerEvent => ({
    id: `income-${income.id}-${toDayKey(date)}`,
    date: on,
    type: 'income',
    amount: income.amount,
    label: income.name,
    source: { kind: 'income', incomeId: income.id },
  });
  const expenseEvent = (expense: Expense, date: Date, on: Date): LedgerEvent => ({
    id: `expense-${expense.id}-${toDayKey(date)}`,
    date: on,
    type: 'expense',
    amount: expense.amount,
    label: expense.name,
    source: { kind: 'expense', expenseId: expense.id, category: expense.category },
  });
  const loanEvent = (loan: Loan, date: Date, on: Date): LedgerEvent => ({
    id: `loan-${loan.id}-${toDayKey(date)}`,
    date: on,
    type: 'expense',
    amount: loan.monthlyPayment,
    label: `${loan.name} Payment`,
    source: { kind: 'loan', loanId: loan.id },
  });

  const totalDays = daysBetween(computeFrom, computeTo);
  for (let i = 0; i <= totalDays; i += 1) {
    const date = addDays(computeFrom, i);

    for (const income of incomes) {
      if (!incomeOccursOn(income, date)) continue;
      if (isMovedAway('income', income.id, date)) continue;
      push(date, incomeEvent(income, date, date));
    }

    for (const expense of expenses) {
      if (!expenseOccursOn(expense, date)) continue;
      if (isMovedAway('expense', expense.id, date)) continue;
      push(date, expenseEvent(expense, date, date));
    }

    for (const loan of loans) {
      if (!loanPaymentOccursOn(loan, date)) continue;
      if (isMovedAway('loan', loan.id, date)) continue;
      push(date, loanEvent(loan, date, date));
    }

    // Occurrences that moved ONTO this day, from anywhere.
    for (const ex of movedOnto.get(toDayKey(date)) ?? []) {
      const was = ex.originalDate;
      if (ex.kind === 'income') {
        const income = incomes.find(x => x.id === ex.sourceId);
        if (income && incomeOccursOn(income, was)) push(date, incomeEvent(income, was, date));
      } else if (ex.kind === 'expense') {
        const expense = expenses.find(x => x.id === ex.sourceId);
        if (expense && expenseOccursOn(expense, was)) push(date, expenseEvent(expense, was, date));
      } else {
        const loan = loans.find(x => x.id === ex.sourceId);
        if (loan && loanPaymentOccursOn(loan, was)) push(date, loanEvent(loan, was, date));
      }
    }
  }

  // --- Assemble days ------------------------------------------------------
  const days: LedgerDay[] = [];
  for (let i = 0; i <= totalDays; i += 1) {
    const date = addDays(computeFrom, i);
    const events = (byDay.get(toDayKey(date)) ?? []).slice().sort((a, b) => {
      // Income first, then expenses — reads better on a calendar tile.
      if (a.type !== b.type) return a.type === 'income' ? -1 : 1;
      return 0;
    });
    days.push({
      date,
      balance: 0,
      events,
      belowThreshold: false,
      isNegative: false,
      isHistorical: date < openingDate,
    });
  }

  const netOf = (d: LedgerDay) =>
    d.events.reduce((sum, e) => sum + (e.type === 'income' ? e.amount : -e.amount), 0);

  // The opening balance is END-OF-DAY on openingDate, so that day's own events
  // are already reflected in it and must not be applied again — the
  // "same-day transactions are already included" rule. Getting this wrong is
  // what previously double-counted a paycheck landing on the day the balance
  // was updated.
  //
  // computeFrom/computeTo guarantee the anchor is inside `days`, so there is
  // exactly one forward pass and one backward pass — no edge cases.
  const openingIndex = daysBetween(computeFrom, openingDate);
  days[openingIndex].balance = openingBalance;

  let running = openingBalance;
  for (let i = openingIndex + 1; i < days.length; i += 1) {
    running += netOf(days[i]);
    days[i].balance = running;
  }

  // Backward: balance[d] = balance[d+1] - net(d+1)
  let back = openingBalance;
  for (let i = openingIndex - 1; i >= 0; i -= 1) {
    back -= netOf(days[i + 1]);
    days[i].balance = back;
  }

  for (const day of days) {
    day.isNegative = day.balance < 0;
    day.belowThreshold =
      lowBalanceThreshold > 0 && day.balance < lowBalanceThreshold && day.balance >= 0;
  }

  // Slice off the padding we added to reach the anchor.
  const sliceStart = daysBetween(computeFrom, from);
  const sliceEnd = daysBetween(computeFrom, to);
  return days.slice(sliceStart, sliceEnd + 1);
}

// --- Thin wrappers ---------------------------------------------------------

/** The projected checking balance on a date. */
export function balanceOnDate(
  input: Omit<BuildLedgerInput, 'from' | 'to'> & { targetDate: Date }
): number {
  const target = startOfDay(input.targetDate);
  const opening = startOfDay(input.openingDate);
  const days = buildLedger({
    ...input,
    from: target < opening ? target : opening,
    to: target > opening ? target : opening,
  });
  const match = days.find(d => toDayKey(d.date) === toDayKey(target));
  return match ? match.balance : input.openingBalance;
}

/** Days in the window whose projected balance breaches the threshold. */
export function thresholdBreaches(
  days: LedgerDay[]
): { date: Date; balance: number; isNegative: boolean }[] {
  return days
    .filter(d => !d.isHistorical && (d.belowThreshold || d.isNegative))
    .map(d => ({ date: d.date, balance: d.balance, isNegative: d.isNegative }));
}
